import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '@/hooks/useAuth';
import { useOrganization } from '@/services/organizations';
import { useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useRef, lazy, Suspense } from 'react';

// MKTG-34 — the marketing page is the front door. A signed-out visitor
// landing on the bare domain (orbitstudiocrm.com) sees it AT the root
// URL — no bounce to /auth, no /orbit in the address bar. Lazy so the
// signed-in path never pays for the marketing bundle.
const Marketing = lazy(() => import('@/pages/Marketing'));
import OnboardingWizard from '@/components/onboarding/OnboardingWizard';
// CSV-IMPORT-1 — clients setup held until the plan was live.
import { PendingClientImport } from '@/components/onboarding/PendingClientImport';
import { loadPendingInvite } from '@/lib/pendingInvite';
import OrbitLoader from '@/components/shared/OrbitLoader';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw, LogOut } from 'lucide-react';
import { OrbitIcon } from "@/components/icons/OrbitIcon";
import { syncStorage } from '@/lib/storage';
import { SIGNUP_REQUIRES_PLAN } from '@/config/launch';
import { useSubscription } from '@/hooks/useSubscription';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

// (#336) Per-tab marker that proves we've successfully observed a valid org
// for this user. Survives React tree evictions on long-idle tabs because
// sessionStorage persists for the lifetime of the tab. Lets us tell apart
// "user genuinely needs onboarding" from "useOrganization transiently
// returned null after the browser parked the tab."
const SESSION_ORG_VERIFIED_KEY = 'orbit.org_verified_this_session';

// (#336) If we drop into the recovery state and don't snap back to a valid
// org within this window, do a hard window.location.reload(). This is the
// "or just use the nova spinning loading screen and refresh instead of
// opening this" guidance translated to code — guaranteed escape hatch.
const RECOVERY_HARD_RELOAD_MS = 3500;

// HOTFIX-1 — sessionStorage counter capping the recovery reload above.
//
// WHY THIS EXISTS: the (#336) recovery path was an unbounded loop. Once
// SESSION_ORG_VERIFIED_KEY is set, ANY subsequent state where the org
// query yields null/undefined puts us in `recovering`, which fires
// `window.location.reload()` after 3.5s. sessionStorage survives a
// reload, so if the org query is persistently failing — an RLS change,
// a dropped profiles→organizations binding, Supabase down, the studio's
// org row genuinely gone — the app reloads, waits 3.5s, reloads, waits
// 3.5s, forever. From the outside that is precisely "just loads
// endlessly": a spinner, a flash, a spinner, a flash.
//
// One automatic reload is a legitimate recovery attempt. Two is a loop.
// After the cap we stop and show the studio what actually happened.
const RECOVERY_RELOAD_COUNT_KEY = 'orbit.org_recovery_reloads';
const MAX_RECOVERY_RELOADS = 1;

// HOTFIX-1 — hard ceiling on the "bringing your studio up" spinner.
//
// That spinner is shown while `loading || (user && !onboardingChecked)`,
// and `onboardingChecked` is only ever set inside an effect that
// requires `!loading && !orgLoading && user`. So any gate that never
// reaches a terminal state — a Supabase call that never settles, a
// react-query fetch that hangs mid-retry — parks the studio on a
// spinner with no error, no retry and no timeout. This is the backstop:
// past it, the user gets a real failure screen instead.
//
// 15s > the auth bootstrap timeout (10s) + react-query's retry ladder
// on a fast failure, so a gate that fails *properly* always renders its
// own, more specific error first. This only catches true never-settles.
const SHELL_BOOT_TIMEOUT_MS = 15_000;

function readRecoveryReloads(): number {
  try {
    return Number(sessionStorage.getItem(RECOVERY_RELOAD_COUNT_KEY) ?? '0') || 0;
  } catch {
    // sessionStorage blocked (private mode). Returning the cap is the
    // safe read: we'd rather show the error screen than risk a reload
    // loop we have no way to count.
    return MAX_RECOVERY_RELOADS;
  }
}

/**
 * HOTFIX-1 — the terminal state for every gate below.
 *
 * The rule this enforces: no loading state in the app shell is
 * reachable without a way out. If a gate can't resolve, the studio sees
 * what failed and a button, never an indefinite spinner.
 */
function ShellError({
  title,
  detail,
  onRetry,
}: {
  title: string;
  detail: string;
  onRetry: () => void;
}) {
  const handleSignOut = () => {
    // Deliberately a nuclear reset rather than `signOut()`: we get here
    // when we can't trust the session or the org binding, and the local
    // Supabase token is the most likely thing to be poisoned. Clearing
    // storage and hard-navigating to /auth always works, even when the
    // network path that signOut() needs doesn't.
    try {
      window.localStorage.clear();
      window.sessionStorage.clear();
    } catch {
      /* private mode — the navigation below still gets them out */
    }
    window.location.href = '/auth';
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="card-glass p-8 max-w-md text-center animate-fade-in">
        <div className="relative mb-6 inline-block">
          <div className="absolute inset-0 bg-destructive/20 rounded-full blur-xl" />
          <div className="relative card-glass p-5 rounded-full">
            <OrbitIcon name="exclamationmark.triangle.fill" fallback={AlertTriangle} className="w-10 h-10 text-destructive" />
          </div>
        </div>

        <h2 className="font-editorial text-2xl md:text-3xl text-foreground leading-none mb-3">
          {title}
        </h2>

        <p className="text-muted-foreground mb-4">
          Your work is safe — this is Orbit failing to load, not anything
          you did. Try again, and if it keeps happening, sign out and back in.
        </p>

        {/* Always rendered. A blank error box is worse than an ugly one. */}
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 mb-6 text-left">
          <code className="text-xs text-destructive/80 break-all">{detail}</code>
        </div>

        <div className="flex gap-3 justify-center">
          <Button variant="outline" onClick={handleSignOut}>
            <OrbitIcon name="rectangle.portrait.and.arrow.right" fallback={LogOut} className="w-4 h-4 mr-2" />
            Sign out
          </Button>
          <Button onClick={onRetry}>
            <OrbitIcon name="arrow.clockwise" fallback={RefreshCw} className="w-4 h-4 mr-2" />
            Try again
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  // HOTFIX-1 — `sessionError` / `retrySession` are new; see useAuth.tsx.
  const { user, loading, sessionError, retrySession } = useAuth();
  const location = useLocation();
  const {
    data: organization,
    isLoading: orgLoading,
    // HOTFIX-1 — the org query THROWS on a Supabase error
    // (src/services/organizations.ts) and react-query retries it 3x
    // before surfacing. Nothing read the error, so a hard failure fell
    // through to `needsSetup` and was treated as "this studio has never
    // onboarded" — which either bounced a paying studio into the setup
    // wizard or, if the session marker was set, into the reload loop.
    error: orgError,
    refetch,
  } = useOrganization();
  const queryClient = useQueryClient();
  const [showOnboarding, setShowOnboarding] = useState(false);
  // HOTFIX-4 — true when the forced wizard should open directly on the
  // "Set up your business" step: the user's first-run flags are already
  // stamped (they watched or skipped the intro material), so replaying
  // the welcome steps would be noise — the only thing missing is the org.
  const [wizardStartsAtStudio, setWizardStartsAtStudio] = useState(false);
  // SIGNUP-1 — set when the org is fine but the account was never
  // finished, which now means "no plan". Opens the wizard on the plan
  // step rather than replaying setup they already did.
  const [wizardStartsAtPlan, setWizardStartsAtPlan] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  // PAYWALL-1 — the plan verdict comes from the server, not localStorage.
  // `useCheckSubscription` is one react-query entry per user (1-minute
  // staleTime), already fetched by Billing, the feature gates and the
  // wizard, so reading it here costs nothing new on a warm cache and
  // one round-trip on a cold one.
  const {
    hasServerPlan,
    isLoading: subscriptionLoading,
    subscriptionUnavailable,
  } = useSubscription();
  const [recovering, setRecovering] = useState(false);
  // HOTFIX-1 — set when a gate blew past SHELL_BOOT_TIMEOUT_MS.
  const [bootTimedOut, setBootTimedOut] = useState(false);
  // HOTFIX-1 — set when the recovery path has already used up its one
  // automatic reload and still has no org.
  const [recoveryExhausted, setRecoveryExhausted] = useState(false);
  // Refs avoid re-firing the recovery effect when state nudges happen for
  // unrelated reasons.
  const reloadTimerRef = useRef<number | null>(null);

  // (#336) Whenever we observe a complete org, stamp the session marker.
  // This is the *only* way the marker gets set — it's earned, not assumed.
  // Doing it in a separate effect (not the main guard) keeps the write
  // independent of whether the user passes the onboarding-check.
  useEffect(() => {
    if (organization?.name && organization?.slug) {
      sessionStorage.setItem(SESSION_ORG_VERIFIED_KEY, 'true');
      // HOTFIX-1 — a healthy org resets the reload budget, so a later
      // unrelated blip in this same tab still gets its one free retry.
      try {
        sessionStorage.removeItem(RECOVERY_RELOAD_COUNT_KEY);
      } catch {
        /* private mode — nothing to clear */
      }
      if (recovering) {
        // We were in the spinner state but the org just came back — exit
        // recovery and let the children mount normally.
        setRecovering(false);
        if (reloadTimerRef.current !== null) {
          window.clearTimeout(reloadTimerRef.current);
          reloadTimerRef.current = null;
        }
      }
    }
  }, [organization, recovering]);

  useEffect(() => {
    if (!loading && !orgLoading && user) {
      // HOTFIX-1 — a failed org lookup is NOT "needs onboarding". Mark
      // the check done so the boot spinner clears, and let the error
      // branch in the render below take it from here.
      if (orgError) {
        setOnboardingChecked(true);
        return;
      }

      const completed = syncStorage.get('onboarding_complete');
      const sessionVerified =
        sessionStorage.getItem(SESSION_ORG_VERIFIED_KEY) === 'true';

      const needsSetup =
        !organization || !organization.name || !organization.slug;

      // (#336) Tab-resume safety. If we've ever observed a valid org in
      // this tab session, a null/empty result here is a transient failure
      // (React tree evicted on idle, RPC race, network blip). NEVER bounce
      // to onboarding from this path — show the NovaSpinner recovery
      // screen, force-invalidate + refetch the org query, and let the
      // hard-reload safety net catch us if the refetch doesn't recover.
      if (sessionVerified && needsSetup) {
        // HOTFIX-1 — but only if we still have reload budget. Otherwise
        // go straight to the error screen; re-entering `recovering`
        // would just queue another reload of a page we already know
        // doesn't come back.
        if (readRecoveryReloads() >= MAX_RECOVERY_RELOADS) {
          setRecoveryExhausted(true);
          setOnboardingChecked(true);
          return;
        }

        setRecovering(true);
        queryClient.invalidateQueries({
          queryKey: ['organization', user.id],
        });
        // Fire the refetch explicitly so we don't have to wait for the
        // next refetchOnWindowFocus cycle (the org query has that off).
        refetch();
        setOnboardingChecked(true);
        return;
      }

      // HOTFIX-4 — org creation is NOT skippable. This gate used to
      // trust the local `onboarding_complete` flag (`completed !==
      // 'true' && needsSetup`), but the wizard's Skip — and the
      // /onboarding route's — stamped that flag without an org row
      // existing. A fresh account could Skip its way onto Today,
      // where the dashboard renders and every write dies with
      // "Organization not found". A SUCCESSFUL org query that
      // returns no valid org now always forces the wizard, flag or
      // no flag.
      //
      // The HOTFIX-1 distinction is intact: a FAILED org query never
      // reaches this line — it returned above via `orgError` and
      // renders ShellError with retry, not the setup wizard. And the
      // (#336) transient-null recovery path returned above too, so
      // an established studio still never bounces into setup.
      if (needsSetup) {
        // First-run flags stamped → they've already seen (or chosen
        // to skip) the intro material. Land them straight on the
        // "Set up your business" step instead of replaying the tour.
        setWizardStartsAtStudio(completed === 'true');
        setShowOnboarding(true);
      } else if (SIGNUP_REQUIRES_PLAN) {
        // PAYWALL-1 — THE ORG EXISTS. DOES IT HAVE A PLAN? ASK THE SERVER.
        //
        // SIGNUP-1 gated this on `onboarding_complete` in localStorage
        // and deliberately skipped the subscription lookup. That stamp
        // is per-BROWSER, not per-account: sign up a second account on
        // a laptop that ever finished setup and the new org walks
        // straight in, plan step never seen. It is also one DevTools
        // line away for anyone. A flag the client writes cannot be the
        // thing the paywall reads.
        //
        // So: `hasServerPlan` is `effective_plan_for_org !== 'free'`,
        // resolved in Postgres from org_subscriptions + tier
        // entitlements (full_access, grant_*_tier). Nothing the client
        // holds can lift it — not the demo overlay, not the stamp.
        //
        // Three outcomes:
        //   loading      → keep the boot spinner; the HOTFIX-1 timeout
        //                  still backstops a hung request.
        //   unavailable  → the function could not be reached. Fall back
        //                  to the stamp so a paying studio isn't locked
        //                  out by a network blip. This is the ONLY path
        //                  where the stamp still opens the account.
        //   free         → the wall, whatever the stamp says.
        if (subscriptionLoading) return;
        const planVerified =
          hasServerPlan || (subscriptionUnavailable && completed === 'true');
        if (!planVerified) {
          setWizardStartsAtPlan(true);
          setShowOnboarding(true);
        }
      }

      setOnboardingChecked(true);
    }
  }, [
    loading,
    orgLoading,
    user,
    organization,
    orgError,
    queryClient,
    refetch,
    subscriptionLoading,
    hasServerPlan,
    subscriptionUnavailable,
  ]);

  // (#336) Hard-reload safety net for the recovery state. If refetch
  // doesn't restore a valid org within RECOVERY_HARD_RELOAD_MS, force a
  // full page reload — this rebuilds auth, query cache, and React tree
  // from scratch, which is more reliable than trying to surgically nudge
  // the cache back to life from an unknown bad state.
  //
  // HOTFIX-1 — the reload is now COUNTED and CAPPED. See
  // RECOVERY_RELOAD_COUNT_KEY above for why an uncapped version of this
  // is itself an "endless loading" bug.
  useEffect(() => {
    if (!recovering) return;
    reloadTimerRef.current = window.setTimeout(() => {
      const used = readRecoveryReloads();
      if (used >= MAX_RECOVERY_RELOADS) {
        setRecoveryExhausted(true);
        setRecovering(false);
        return;
      }
      try {
        sessionStorage.setItem(RECOVERY_RELOAD_COUNT_KEY, String(used + 1));
      } catch {
        /* private mode — the read helper returns the cap, so we stop */
      }
      window.location.reload();
    }, RECOVERY_HARD_RELOAD_MS);
    return () => {
      if (reloadTimerRef.current !== null) {
        window.clearTimeout(reloadTimerRef.current);
        reloadTimerRef.current = null;
      }
    };
  }, [recovering]);

  // HOTFIX-1 — the backstop. Runs only while the boot spinner would be
  // on screen, and unwinds itself the moment the shell settles, so a
  // healthy boot never pays for it.
  const bootSpinnerShowing = loading || (!!user && !onboardingChecked);
  useEffect(() => {
    if (!bootSpinnerShowing) {
      setBootTimedOut(false);
      return;
    }
    const id = window.setTimeout(
      () => setBootTimedOut(true),
      SHELL_BOOT_TIMEOUT_MS,
    );
    return () => window.clearTimeout(id);
  }, [bootSpinnerShowing]);

  const retryEverything = () => {
    setBootTimedOut(false);
    setRecoveryExhausted(false);
    setRecovering(false);
    setOnboardingChecked(false);
    try {
      sessionStorage.removeItem(RECOVERY_RELOAD_COUNT_KEY);
    } catch {
      /* private mode */
    }
    retrySession();
    queryClient.invalidateQueries({ queryKey: ['organization'] });
    refetch();
  };

  // ── Terminal failure branches. These come FIRST, on purpose: every
  //    one of them is a state the old code could reach while still
  //    rendering a spinner. ─────────────────────────────────────────

  // HOTFIX-1 — we could not determine the session (rejected, errored, or
  // timed out). Note this is NOT treated as "signed out": redirecting to
  // /auth on a network blip looks like a random forced sign-out, and the
  // user would sign in again straight into the same failure.
  if (sessionError) {
    return (
      <ShellError
        title="We couldn't reach your account."
        detail={sessionError.message}
        onRetry={retryEverything}
      />
    );
  }

  if (bootTimedOut) {
    return (
      <ShellError
        title="Orbit is taking too long to start."
        detail={`The app shell waited ${Math.round(
          SHELL_BOOT_TIMEOUT_MS / 1000,
        )}s for your session and studio to load and got no answer either way.`}
        onRetry={retryEverything}
      />
    );
  }

  if (loading || (user && !onboardingChecked)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <OrbitLoader size="lg" label="One sec — bringing your studio up." />
      </div>
    );
  }

  // DOMAIN-5 — `www` is now the canonical APP host (hosting 308s the
  // bare domain to it), so it can no longer be brochure-only: a
  // signed-in studio landing on '/' must get the app. Signed-out
  // visitors still fall through to the marketing page below.


  if (!user) {
    // MKTG-34 — bare domain -> marketing page, in place. Every other
    // protected path keeps bouncing to /auth as before.
    if (location.pathname === '/') {
      return (
        <Suspense fallback={null}>
          <Marketing />
        </Suspense>
      );
    }
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // TEAM-1 — someone who opened a team invite before they had an
  // account goes back to it first. Google / Apple sign-in and the
  // confirm-email link land on `/`, where setup would otherwise make them
  // a business of their own before they ever saw the invite. The invite
  // page forgets it once it's accepted, turned down or gone.
  const pendingInvite = loadPendingInvite();
  if (pendingInvite) {
    return <Navigate to={`/invite/${pendingInvite}`} replace />;
  }

  // HOTFIX-1 — the org lookup itself failed. Previously this fell
  // through into `needsSetup` and either showed the setup wizard to an
  // established studio or started the reload loop.
  if (orgError) {
    return (
      <ShellError
        title="We couldn't load your studio."
        detail={
          orgError instanceof Error
            ? orgError.message
            : 'Your studio didn’t load.'
        }
        onRetry={retryEverything}
      />
    );
  }

  // HOTFIX-1 — recovery ran out of road. One automatic reload already
  // happened and the org still isn't there, so stop reloading and say so.
  if (recoveryExhausted) {
    return (
      <ShellError
        title="We couldn't reconnect your studio."
        detail={
          'Your account is signed in, but the studio it belongs to did not load ' +
          'after a retry and a refresh. This usually means the account is not ' +
          'linked to a studio yet.'
        }
        onRetry={retryEverything}
      />
    );
  }

  // (#336) Recovery state — the full thinking-Nova loader is more on-brand
  // than the small inline spinner for an idle-resume moment. The hard
  // reload below will fire if this lingers.
  if (recovering) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <OrbitLoader size="lg" label="Reconnecting…" />
      </div>
    );
  }

  if (showOnboarding) {
    return (
      <>
      {/* CSV-IMPORT-1 — beside setup too: "Welcome aboard" is where a
          held import usually lands, right after checkout. */}
      <PendingClientImport />
      <OnboardingWizard
        // HOTFIX-4 — forced setup for an account that already skipped
        // the intro jumps straight to org creation.
        initialStepKey={
          wizardStartsAtPlan ? 'plan' : wizardStartsAtStudio ? 'studio' : undefined
        }
        onComplete={(landingPath) => {
          syncStorage.set('onboarding_complete', 'true');
          // Also stamp the session marker on first-time completion so the
          // very next render in this tab doesn't re-trip the guard before
          // useOrganization catches up.
          sessionStorage.setItem(SESSION_ORG_VERIFIED_KEY, 'true');
          // SIGNUP-7 — honour where the wizard wants to land. Stripe
          // returns every new studio to `/?checkout=success`, which is
          // THIS mount (not the /onboarding route), so ignoring the
          // argument meant nobody ever saw the tour: Welcome aboard
          // asked for /tour/orbit and got Today.
          if (landingPath && landingPath !== '/' && landingPath !== location.pathname) {
            window.location.assign(landingPath);
            return;
          }
          setShowOnboarding(false);
        }}
      />
      </>
    );
  }

  return (
    <>
      <PendingClientImport />
      {children}
    </>
  );
}
